import { Action, AnyAction, Dispatch, Store } from 'redux';
import { HeaderState } from 'models/HeaderState';
import { PageState } from 'models/PageState';
import { uid, isBrowser } from 'utils';
import warning from 'warning';

type Callback = <S extends HeaderState = PageState, A extends Action = AnyAction>(
  dispatch: Dispatch<A>,
  getState: () => S
) => void;

const prefix = '@@broadcast/';

const own = new Set<string>();
const listeners: { [key: string]: Callback[] } = {};

let store: Store<HeaderState>;
export function initBroadcast<S extends HeaderState>(s: Store<S>) {
  store = s;
}
(function (isBrowser: boolean) {
  if (!isBrowser) {
    return;
  }
  let timer: number;
  let callbacks: Callback[] = [];
  const listener = () => {
    if (!document.hidden && callbacks.length) {
      callbacks
        .splice(0, callbacks.length)
        .forEach(cb => cb(store.dispatch, store.getState.bind(store)));
      window.clearTimeout(timer);
    }
  };
  window.addEventListener('storage', ({ key, newValue }) => {
    if (!key || key.indexOf(prefix) !== 0 || !newValue) {
      return;
    }
    if (own.has(newValue)) {
      own.delete(newValue);
      return;
    }

    const event = key.substr(prefix.length);
    if (event in listeners) {
      callbacks = callbacks.concat(listeners[event].filter(e => !callbacks.includes(e)));
    }

    window.clearTimeout(timer);
    timer = window.setTimeout(listener, 0);
  });
  document.addEventListener('visibilitychange', listener);
})(isBrowser());

export function send(events: string | string[]) {
  warning(!!store, 'store not initiated. Can not send tab messages');
  (Array.isArray(events) ? events : [events]).forEach(e => {
    const id = uid();
    own.add(id);
    localStorage.setItem(`${prefix}${e}`, id);
    localStorage.removeItem(`${prefix}${e}`);
  });
}

export function subscribe(events: string | string[], cb: Callback): () => void {
  const e = Array.isArray(events) ? events : [events];
  e.forEach(function (e) {
    if (!(e in listeners)) {
      listeners[e] = [];
    }
    listeners[e].push(cb);
  });

  return function unsubscribe() {
    e.forEach(function (e) {
      const i = listeners[e].indexOf(cb);
      if (i > -1) {
        listeners[e].splice(i, 1);
      }
    });
  };
}
